import { MediaMatcher } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgIf, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { mustMatch } from '../../validators/must-match-validator';
import { getUrlPathWithSuffix, pathNames, variablePathNames } from '../../utilities/routing-path-utils';
import { UserService } from '../../services/user.service';
import { ClientRegistrationRequest } from '../../models/requests/client-registration-request';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from '../../environments/environments';
import { SubscriptionHelper } from '../../classes/subscription-helper';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    NgStyle,
    MatProgressSpinnerModule,
    NgIf,
    RouterLink,
    MatSnackBarModule,
    NgClass
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent {
  hide: boolean = true;
  isLoginLoading: boolean = false;
  signupForm!: FormGroup;
  activationLink: string | undefined = undefined
  loginPath: string = getUrlPathWithSuffix(pathNames.loginPath);
  renderConfirmationEvent = output<any>();
  desktopQuery: MediaQueryList;
  mobileQuery: MediaQueryList;

  private _queryListener: () => void;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private route: ActivatedRoute
  ) {
    this.desktopQuery = media.matchMedia('(min-width: 1100px)');
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._queryListener = () => changeDetectorRef.detectChanges();
    this.desktopQuery.addEventListener('change', this._queryListener);
    this.mobileQuery.addEventListener('change', this._queryListener);
  }

  ngOnInit() {
    this.initForm();
    this.setLinkId();
    this.initLoginPath();
  }

  ngOnDestroy(){
    this.desktopQuery.removeEventListener('change', this._queryListener);
    this.mobileQuery.removeEventListener('change', this._queryListener);
  }

  initLoginPath(){
    if (this.activationLink) {
      this.loginPath = getUrlPathWithSuffix('../' + pathNames.loginPath);
    }
  }

  initForm() {
    this.signupForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(60)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(60)]]
    });

    this.signupForm.setValidators(mustMatch('password', 'confirmPassword'));
  }

  onGoogleSignUp() {
    window.open(`${environment.baseUrl}/auth/google`, "_self");
  }

  onSubmit() {
    if (this.signupForm.valid) {
      const values = this.signupForm.value;

      const userSignUpDTO: ClientRegistrationRequest = {
        email: values['email'],
        password: values['password'],
        firstName: values['firstName'],
        lastName: values['lastName'],
        linkId: this.activationLink
      }

      this.callAppropriateSignupEndpoint(userSignUpDTO)
    }
  }

  callAppropriateSignupEndpoint(userSignUpDTO: ClientRegistrationRequest) {
    this.isLoginLoading = true;

    if (!userSignUpDTO.linkId) {
      this.userService.createUser(userSignUpDTO).subscribe({
        next: () => {
          this.renderConfirmationEvent.emit(null);
        },
        error: () => {
          this.isLoginLoading = false;
          this.snackBar.open('Error email must be unique', 'Close', { duration: 5000 });
        }
      });
    } else {
      this.userService.createSubaccountUser(userSignUpDTO).subscribe({
        next: () => {
          this.renderConfirmationEvent.emit(null);
        },
        error: () => {
          this.isLoginLoading = false;
          this.snackBar.open('Error email must be unique', 'Close', { duration: 5000 });
        }
      });
    }
  }

  setLinkId() {
    this.route.paramMap.subscribe(params => {
      this.activationLink = params.get(variablePathNames.subaccountPathVariable) || undefined;
    });
  }
}
